import { computed, ComputedRef, Ref, unref, ref } from 'vue'
import { ApolloClient } from '@apollo/client/core'
import { TypedDocumentNode } from '@graphql-typed-document-node/core'
import {
  CompanyAccountsQuery,
  CompanyFragmentDef,
  FieldTypeFragmentDef,
  FullTypeFragmentDef,
  InputValueFragmentDef,
  IntrospectionQuery,
  OrganisationFragmentDef,
} from '@/components/GraphqlDataTable/queries'
import { useFragment } from '@/gql'
import {
  FullTypeFragment,
  CompanyAccountFragment,
  InputValueFragment,
  FieldTypeFragment,
  OrganisationAccountFragment,
} from '@/gql/graphql'
import { ListItem } from '@/components/Workflows/Pages/Workflow/workflow'
import { provider } from '@/app-bootstrapping/SetUpApollo'

function customProvide<TResult = any>(
  client: ApolloClient<any>,
  query: TypedDocumentNode<TResult, any>,
) {
  const loading = ref(true)
  const result: Ref<TResult | undefined> = ref()
  client
    .query<TResult>({ query })
    .then((data) => (result.value = data.data))
    .finally(() => (loading.value = false))

  return {
    loading,
    result,
  }
}

/**
 * @param {string} clientName The name of the apollo client we want to grab introspection information on
 */
export const useIntrospectionTypes = (
  clientName: string = 'publicApiCustomFields',
): {
  introspection: any
  typeLookup: ComputedRef<Record<string, FullTypeFragment>>
  queryType: ComputedRef<FullTypeFragment | null>
} => {
  const client = provider.clients[clientName]
  if (!client) throw new Error(`Client not found, was given: ${clientName}`)

  const { loading, result: introspection } = customProvide(client, IntrospectionQuery)

  const typeLookup: ComputedRef<Record<string, FullTypeFragment>> = computed(() => {
    if (loading.value || !introspection.value?.__schema) return {} // Return empty object if data is still loading

    // Destructure types from the introspection result
    const {
      __schema: { types },
    } = introspection.value

    // Reduce the types array into an object with type names as keys
    return (
      types
        .map((type) => useFragment(FullTypeFragmentDef, type))
        .filter(({ name }: FullTypeFragment) => name !== null && name !== undefined)
        // Since we just filtered out nulls and undefineds its safe to do a forced cast here
        .reduce((acc, type) => ({ ...acc, [type.name as string]: type }), {})
    )
  })

  // Contains information on the Query type of the client. Basically contains information on all our endpoints
  const queryType: ComputedRef<FullTypeFragment | null> = computed(() => {
    if (loading.value || !introspection) return null // Return null if we don't have data yet

    // Destructure queries from the introspection result
    return useFragment(FullTypeFragmentDef, introspection.value?.__schema.queryType) ?? null
  })

  return { typeLookup, queryType, introspection }
}

/**
 * Fetches the authenticated users accounts and organisations accounts and returns them all in an array
 * @param {string} clientName The name of the apollo client we want to grab introspection information on
 */
export const useCompanyAccounts = (
  clientName: string = 'publicApiCustomFields',
): {
  accounts: ComputedRef<(ListItem & (CompanyAccountFragment | OrganisationAccountFragment))[]>
  loading: Ref<boolean>
} => {
  const client = provider.clients[clientName]
  if (!client) throw new Error(`Client not found, was given: ${clientName}`)
  const { result, loading } = customProvide(client, CompanyAccountsQuery)

  // Computed property to process and flatten the accounts data
  const accounts: ComputedRef<
    (ListItem & (CompanyAccountFragment | OrganisationAccountFragment))[]
  > = computed(() => {
    const seen = {} // Object to keep track of processed account IDs to avoid duplicates

    return (
      result.value?.accounts
        .flatMap((acc) => {
          // If the account is an 'Organisation', include its divisions
          if (acc.__typename === 'Organisation') {
            const org = useFragment(OrganisationFragmentDef, acc)
            // Return an array containing the organisation and its divisions
            return [org, ...org.divisions.data.map((c) => useFragment(CompanyFragmentDef, c))]
          }
          // Otherwise, return the account as is
          if (acc.__typename === 'Company') {
            return [useFragment(CompanyFragmentDef, acc)]
          }
          return []
        })
        .filter((acc) =>
          // Filter out duplicate accounts based on their ID
          acc !== undefined && seen.hasOwnProperty(acc.id) ? false : (seen[acc.id] = true),
        )
        .map((acc: OrganisationAccountFragment | CompanyAccountFragment) => ({
          // Map each account to an object with 'value', 'label', and other account properties
          value: acc.id,
          label: acc.name,
          ...acc,
        })) ?? []
    )
  })

  return { accounts, loading }
}

const ARG_BREAK_TOKEN = '<ARG_BREAK>'
// eslint-disable-next-line
// @ts-expect-error ts hates the named capturing group
const queryArgsReg = /query[^(]+[(](?<args>[^)]+)/
// eslint-disable-next-line
// @ts-expect-error ts hates the named capturing groups
const queryTypeReg = /query.+\{[ \n]+(?<query>[^(]+)\((?<args>[^)]+)/

// Function takes and cleans + normalizes the argument strings, like $foo: Bar, $bar: Foo\n $Cee: $Doo\n
// Will instead become $foo: Bar<ARG_BREAK> $bar: Foo<ARG_BREAK> $Cee: $Doo
// Making it easier to split the arguments cleanly
const argumentCleaning = (argStrs: string | undefined) =>
  argStrs?.trim()?.replaceAll(',', ARG_BREAK_TOKEN)?.replaceAll('\n', ARG_BREAK_TOKEN)

// Returns the main type the query is reciving. Ie are we querying hires, then it returns the Hire type information
// It also returns the arguments that are part of the query. Including the inputted args and all args that are part of
// the query itself.
export const useQueryType = (
  query: string | Ref<string>,
  clientName: string = 'publicApiCustomFields',
): {
  args: ComputedRef<{ inputtedArgs: InputValueFragment[]; allArgs: InputValueFragment[] }>
  type: ComputedRef<null | FieldTypeFragment>
} => {
  const qRegs = computed(() => {
    const q = unref(query)
    const argsStr = q.match(queryArgsReg)?.groups?.args
    const { query: queryTypeStr, args: queryArgsDefStr } = q.match(queryTypeReg)?.groups ?? {}

    const args =
      argumentCleaning(argsStr)
        ?.split(ARG_BREAK_TOKEN)
        ?.map((arg) => arg.trim().split(':'))
        ?.map(([name, typeStr]) => ({ name, typeStr })) ?? []

    const queryArgsMap = argumentCleaning(queryArgsDefStr)
      ?.split(ARG_BREAK_TOKEN)
      .map((arg) => arg.trim().split(':'))
      .reduce((acc, [argName, inputArg]) => ({ ...acc, [inputArg.trim()]: argName }), {})

    return { args, queryTypeStr, queryArgsDefStr, queryArgsMap }
  })

  const { queryType } = useIntrospectionTypes(clientName)

  const type: ComputedRef<null | FieldTypeFragment> = computed(() => {
    const typeSearched = queryType.value?.fields
      ?.map((f) => useFragment(FieldTypeFragmentDef, f))
      .find(({ name }) => qRegs.value.queryTypeStr === name)
    if (!typeSearched) return null
    return typeSearched
  })

  const args: ComputedRef<{ inputtedArgs: InputValueFragment[]; allArgs: InputValueFragment[] }> =
    computed(() => {
      const { args: argNames, queryArgsMap } = qRegs.value

      const allArgs = type.value?.args.map((a) => useFragment(InputValueFragmentDef, a)) ?? []
      // These are the arguments that have been inputted in the query.
      const inputtedArgs: InputValueFragment[] = argNames
        .map(({ name: argName }) => allArgs.find(({ name }) => queryArgsMap?.[argName] === name))
        .filter((v): v is InputValueFragment => v !== undefined)

      return { inputtedArgs, allArgs }
    })

  return { type, args }
}
